import { el } from '../dom'
import { DURATION_MINUTES, type DurationMinutes } from '../games/types'
import { COPY, minutesLabel } from './copy'

export function createParentPanel(opts: {
  durationMin: DurationMinutes
  muted: boolean
  onDuration: (minutes: DurationMinutes) => void
  onMute: (muted: boolean) => void
  onEndSession: () => void
  onResetLevels: () => void
  onDone: () => void
}): {
  root: HTMLElement
  open: () => void
  close: () => void
  setMuted: (muted: boolean) => void
  setDuration: (minutes: DurationMinutes) => void
} {
  const durationBtns = DURATION_MINUTES.map((minutes) => {
    const btn = el('button', {
      className: 'chip',
      text: minutesLabel(minutes),
      testId: `duration-${minutes}`,
      attrs: { type: 'button' },
    })
    btn.addEventListener('click', () => opts.onDuration(minutes))
    return { minutes, btn }
  })

  const mute = el('button', {
    className: 'panel-btn',
    testId: 'mute-toggle',
    attrs: { type: 'button' },
  })
  mute.addEventListener('click', () => opts.onMute(!mute.classList.contains('is-on')))

  const reset = el('button', {
    className: 'panel-btn',
    text: COPY.resetLevels,
    testId: 'reset-levels',
    attrs: { type: 'button' },
  })

  const end = el('button', {
    className: 'panel-btn panel-btn--danger',
    text: COPY.endSession,
    testId: 'end-session',
    attrs: { type: 'button' },
  })
  end.addEventListener('click', () => opts.onEndSession())

  const done = el('button', {
    className: 'panel-btn panel-btn--done',
    text: COPY.done,
    testId: 'panel-done',
    attrs: { type: 'button' },
  })
  done.addEventListener('click', () => opts.onDone())

  const main = el('div', { className: 'panel-main', testId: 'parent-panel-main' }, [
    el('h2', { className: 'panel__title', text: COPY.parentTitle }),
    el('p', { className: 'panel__label', text: COPY.durationLabel }),
    el(
      'div',
      { className: 'chip-row' },
      durationBtns.map((item) => item.btn),
    ),
    mute,
    reset,
    end,
    done,
  ])

  const confirmYes = el('button', {
    className: 'panel-btn panel-btn--danger',
    text: COPY.resetYes,
    testId: 'reset-yes',
    attrs: { type: 'button' },
  })
  const confirmNo = el('button', {
    className: 'panel-btn panel-btn--quiet',
    text: COPY.resetNo,
    testId: 'reset-no',
    attrs: { type: 'button' },
  })
  const confirm = el(
    'div',
    {
      className: 'panel-confirm',
      testId: 'reset-confirm',
      attrs: { hidden: '' },
    },
    [
      el('p', { className: 'panel-confirm__text', text: COPY.resetConfirm }),
      confirmYes,
      confirmNo,
    ],
  )

  const showConfirm = (open: boolean) => {
    main.hidden = open
    confirm.hidden = !open
  }

  reset.addEventListener('click', () => showConfirm(true))
  confirmNo.addEventListener('click', () => showConfirm(false))
  confirmYes.addEventListener('click', () => {
    showConfirm(false)
    opts.onResetLevels()
  })

  const root = el(
    'div',
    {
      className: 'overlay overlay--panel',
      testId: 'parent-panel',
      attrs: { hidden: '' },
    },
    [
      el('div', { className: 'panel', testId: 'parent-panel-card' }, [main, confirm]),
    ],
  )

  const setDuration = (minutes: DurationMinutes) => {
    for (const item of durationBtns) {
      const selected = item.minutes === minutes
      item.btn.classList.toggle('is-selected', selected)
      item.btn.setAttribute('aria-pressed', selected ? 'true' : 'false')
    }
  }

  const setMuted = (muted: boolean) => {
    mute.classList.toggle('is-on', muted)
    mute.textContent = muted ? COPY.muteOn : COPY.muteOff
    mute.setAttribute('aria-pressed', muted ? 'true' : 'false')
  }

  setDuration(opts.durationMin)
  setMuted(opts.muted)

  return {
    root,
    open: () => {
      showConfirm(false)
      root.hidden = false
    },
    close: () => {
      showConfirm(false)
      root.hidden = true
    },
    setMuted,
    setDuration,
  }
}
